import { useState } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { AssetItem } from "@/components/editor/AssetList";
import { formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { useRef } from "react";

const MAX = 3 * 1024 ** 3;
const BINS = [["all", "All"], ["footage", "Footage"], ["audio", "Audio"], ["generated", "Generated"], ["exports", "Exports"]];
const inBin = (bin, a) => bin === "all" ? a.kind !== "mixdown" : bin === "generated" ? ["sfx", "score", "animation", "vfx"].includes(a.kind)
  : bin === "exports" ? ["export", "mixdown"].includes(a.kind) : a.kind === bin;

export const MediaLibrary = ({ projectId, assets, setAssets, onAdd, onAddLayer, onView, onDelete, bin, setBin }) => {
  const input = useRef(null);
  const [progress, setProgress] = useState({});
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (file.size > MAX) return toast.error(`${file.name} is larger than 3 GB`);
    const type = file.type || (/\.(mov|mp4|mkv|webm|m4v)$/i.test(file.name) ? "video/mp4" : /\.(wav|mp3|aac|m4a|flac|ogg)$/i.test(file.name) ? "audio/mpeg" : "");
    try {
      const done = await uploadFile({ projectId, file, name: file.name, type,
        onInit: (asset) => setAssets((a) => [asset, ...a]),
        onProgress: (id, p) => setProgress((s) => ({ ...s, [id]: p })) });
      setAssets((a) => a.map((x) => (x.id === done.id ? done : x)));
      toast.success(`${file.name} uploaded`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFiles = (files) => [...files].forEach(upload);
  const shown = assets.filter((a) => inBin(bin, a));

  return (
    <aside className="w-72 xl:w-80 bg-[#13151C] border-r border-[#2A2E3D] flex flex-col min-h-0" data-testid="media-library">
      <div className="p-3 border-b border-[#2A2E3D]">
        <h3 className="text-[11px] uppercase font-mono-ui tracking-wider text-slate-400 mb-2">Media library</h3>
        <button onClick={() => input.current.click()} data-testid="upload-button"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(e.dataTransfer.files); }}
          className={`w-full border border-dashed rounded-md p-4 flex flex-col items-center gap-1 transition-colors ${drag ? "border-[#FFB800] bg-[#FFB800]/5" : "border-[#3F455A] hover:border-[#FFB800]/70"}`}>
          <UploadCloud className="w-5 h-5 text-[#FFB800]" />
          <span className="text-xs">Import footage or audio</span>
          <span className="font-mono-ui text-[10px] text-slate-500">DRAG & DROP · MAX 3 GB</span>
        </button>
        <input ref={input} type="file" multiple accept="video/*,audio/*" className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="upload-input" />
        <div className="flex flex-wrap gap-1 mt-3">
          {BINS.map(([k, l]) => (
            <button key={k} onClick={() => setBin(k)} data-testid={`bin-${k}`}
              className={`text-[11px] px-2 py-1 rounded transition-colors ${bin === k ? "bg-[#FFB800] text-[#0B0C10]" : "text-slate-400 hover:bg-white/5"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        {shown.length ? (
          <div className="space-y-2">
            {shown.map((a) => (
              <div key={a.id}>
                <AssetItem asset={a} onAdd={onAdd} onAddLayer={onAddLayer} onView={onView} onDelete={onDelete} progress={a.status === "uploading" ? progress[a.id] || 0 : undefined} />
                {a.status === "uploading" && <p className="font-mono-ui text-[10px] text-slate-400 mt-1" data-testid={`upload-progress-${a.id}`}>UPLOADING {progress[a.id] || 0}%</p>}
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-slate-500 border border-dashed border-[#2A2E3D] rounded-md p-4 text-center">Nothing here yet.</p>}
      </div>
    </aside>
  );
};
